import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { fetchBomDetail } from '#/api/momMgmt/settings/bom/fetchDetail';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import SideMsg from '#/modules/commonComponents/SideMsg';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  bomSideMsgDetailBlock,
  bomSideMsgStatusBlock,
} from '#/modules/momMgmt/settings/bom/data/bomSideMsgConfig';
import {
  canActivateBom,
  canDeactivateBom,
} from '#/modules/momMgmt/settings/bom/helper/bomStatus';
import useBomStatusMutation, {
  BOM_ACTIVATE_CONFIRM,
  BOM_DEACTIVATE_CONFIRM,
} from '#/modules/momMgmt/settings/bom/hooks/useBomStatusMutation';

const BomSideMsg = ({ displayData, setSelectedData }) => {
  const navigate = useNavigate();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canCreate = useCan(PERM.BOM_CREATE);
  const canActivatePerm = useCan(PERM.BOM_ACTIVATE);

  const id = displayData?.id;
  const status = displayData?.status;
  const updatedAt = displayData?.record?.updatedAt ?? displayData?.updatedAt;
  const canActivate = canActivatePerm && canActivateBom(status);
  const canDeactivate = canActivatePerm && canDeactivateBom(status);
  const canCopy = Boolean(id) && canCreate;
  const canOpenDetail = Boolean(id);

  const statusMutation = useBomStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onAfterSuccess: async () => {
      if (!id) {
        return;
      }
      try {
        const detail = await fetchBomDetail(id);
        setSelectedData(detail?.data ?? null);
      } catch {
        setSelectedData(null);
      }
    },
    onVersionConflict: () => {
      if (!id) {
        return;
      }
      fetchBomDetail(id)
        .then((detail) => setSelectedData(detail?.data ?? null))
        .catch(() => setSelectedData(null));
    },
  });

  const isPending = statusMutation.isPending;

  async function handleActivate() {
    const ok = await confirm(BOM_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(BOM_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  const hasActions = canActivate || canDeactivate || canCopy || canOpenDetail;

  function handleCopy() {
    if (!id) {
      return;
    }
    navigate(ROUTES.bomCopy(id));
  }

  const blocks = [
    bomSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <div className="flex flex-wrap items-center gap-2">
                {canOpenDetail ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.bomDetail(id)}>詳情</Link>
                  </Button>
                ) : null}
                {canCopy ? (
                  <Button
                    type="button"
                    disabled={isPending}
                    onClick={handleCopy}
                  >
                    複製
                  </Button>
                ) : null}
                {canActivate ? (
                  <Button type="button" disabled={isPending} onClick={handleActivate}>
                    啟用
                  </Button>
                ) : null}
                {canDeactivate ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={isPending}
                    onClick={handleDeactivate}
                  >
                    停用
                  </Button>
                ) : null}
              </div>
            </div>
          ),
        },
      ],
    },
    bomSideMsgDetailBlock,
  ];

  return (
    <>
      {ConfirmDialogHost}
      <SideMsg
        title="BOM"
        titleValue="code"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

BomSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default BomSideMsg;
